import { Modal, Skeleton, TextOverflow } from '@sequencehq/core-components'
import { Footer } from './common/Footer'
import { Box, Grid } from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { useBillingScheduleId } from 'BillingScheduleHome/utils/useBillingScheduleId'
import { useBillingScheduleAggregate } from 'BillingScheduleHome/utils/useBillingScheduleAggregate'
import {
  Duration,
  format,
  formatDuration,
  parse
} from '@sequencehq/utils/dates'
import { parseBillingSchedulePhaseDuration } from 'lib/schedules/parseBillingSchedulePhaseDuration'
import { GreyGrey80, Lato13Bold } from '@sequencehq/design-tokens'
import { RadioGroup } from '../../../common/RadioGroup'
import { useForm } from '@sequencehq/utils'
import { required } from '@sequencehq/validation'
import { SectionError } from 'BillingScheduleHome/view/BillingScheduleHomeContent/common/SectionError'
import { cubeUpdateShortcuts } from 'Cube/index'
import { DATE_FORMAT_WITH_YEARS } from '../editModalShortcuts.constants'

type FormData = {
  phaseIdx: string
}

const getPhaseDateRangeLabel = (phase: {
  startDate: Date
  duration: Duration | 'OPEN_ENDED' | 'MILESTONE'
  endDate?: Date
}) => {
  if (typeof phase.duration === 'string') {
    return `${format(phase.startDate as Date, DATE_FORMAT_WITH_YEARS)} → ${phase.duration === 'MILESTONE' ? 'Milestone' : 'Open-ended'}`
  }

  return `${format(phase.startDate as Date, DATE_FORMAT_WITH_YEARS)} → ${format(phase.endDate as Date, DATE_FORMAT_WITH_YEARS)}`
}

const useRenewScreen = () => {
  const navigate = useNavigate()
  const billingScheduleId = useBillingScheduleId()
  const billingScheduleAggregateQuery = useBillingScheduleAggregate({
    billingScheduleId,
    select: data => {
      const phases = data.billingSchedule.phases.map(phase => {
        const startDate = parse(phase.startDate, 'yyyy-MM-dd', new Date())
        const endDate = phase.endDate
          ? parse(phase.endDate, 'yyyy-MM-dd', new Date())
          : undefined
        const duration = parseBillingSchedulePhaseDuration({
          startDate,
          endDate
        })

        return {
          id: phase.id,
          startDate,
          endDate,
          name: phase.name,
          dates: getPhaseDateRangeLabel({
            startDate,
            endDate,
            duration
          }),
          duration,
          isMilestone: duration === 'MILESTONE',
          priceNames: phase.priceIds
            .map(priceId => {
              const price = data.prices.find(price => price.id === priceId)
              return price ? price.name : ''
            })
            .filter(Boolean)
        }
      })

      const firstMilestonePhase = phases.findIndex(phase => phase.isMilestone)

      return {
        phases:
          firstMilestonePhase > -1
            ? [
                ...phases.slice(0, firstMilestonePhase + 1),
                ...phases.slice(firstMilestonePhase + 1).map(p => ({
                  ...p,
                  dates:
                    typeof p.duration !== 'string'
                      ? formatDuration({
                          ...p.duration,
                          days: p.duration.days === -1 ? 0 : p.duration.days
                        })
                      : ''
                }))
              ]
            : phases
      }
    }
  })

  const form = useForm<FormData>({
    value: {
      phaseIdx: '0'
    },
    fieldConfiguration: [
      {
        property: 'phaseIdx',
        validation: [required]
      }
    ]
  })

  const onSubmit = () => {
    if (!form.queries.isValid) {
      return
    }

    const shortcutUuid = cubeUpdateShortcuts.set({
      type: 'renew',
      phaseIndex: parseInt(form.queries.formData.phaseIdx, 10)
    })

    navigate(
      `/billing-schedules/${billingScheduleId}/edit?${cubeUpdateShortcuts.queryParam}=${shortcutUuid}`
    )
  }

  return {
    phases: billingScheduleAggregateQuery.data?.phases ?? [],
    isLoading: billingScheduleAggregateQuery.isPending,
    isError: billingScheduleAggregateQuery.isError,
    formFields: form.fields,
    onSubmit
  }
}

export const RenewScreen = () => {
  const { phases, isLoading, isError, formFields, onSubmit } = useRenewScreen()

  if (isError) {
    return (
      <Modal.Content>
        <SectionError
          text="Could not load schedule phase information"
          size="medium"
        />
      </Modal.Content>
    )
  }

  if (isLoading) {
    return (
      <Modal.Content>
        <Grid gridTemplateRows="auto auto auto" gap="12px">
          <Skeleton width="120px" height="24px" />
          <Box>
            <Skeleton width="100%" height="68px" />
            <Skeleton width="100%" height="68px" />
            <Skeleton width="100%" height="68px" />
          </Box>
        </Grid>
      </Modal.Content>
    )
  }

  return (
    <>
      <Modal.Content maxHeight="600px">
        <Grid gridTemplateRows="auto auto auto" gap="12px">
          <Box {...Lato13Bold} color={GreyGrey80}>
            Choose the phase to renew
          </Box>
          <RadioGroup
            name="phaseIdx"
            onChange={newValue => formFields.phaseIdx.onChange(newValue)}
            value={formFields.phaseIdx.value}
            options={phases.map((phase, idx) => ({
              label: phase.name || phase.dates,
              value: `${idx}`,
              labelAnnotation: phase.name ? phase.dates : undefined,
              description: (
                <TextOverflow splitPercentage={100}>
                  {phase.priceNames.join(', ')}
                </TextOverflow>
              )
            }))}
          />
        </Grid>
      </Modal.Content>
      <Footer onContinue={onSubmit} />
    </>
  )
}
